// Words that carry no client identity. A caseload question is mostly these, and without dropping them
// a query like "who has a DNR on file, and is it current?" would surface a client called Anderson on
// the strength of the word "and". Deliberately excludes words that are also plausible names (May,
// Read, Will, Bell), because suppressing a real surname is worse than one spurious row.
const QUESTION_WORDS = new Set([
    'which', 'who', 'whom', 'whose', 'what', 'when', 'where', 'why', 'how',
    'is', 'are', 'was', 'were', 'has', 'have', 'had', 'does', 'did', 'been',
    'can', 'could', 'should', 'would', 'the', 'and', 'but', 'for', 'from',
    'any', 'all', 'some', 'each', 'every', 'with', 'about', 'that', 'this',
    'these', 'those', 'their', 'them', 'they', 'his', 'her', 'hers', 'its',
    'client', 'clients', 'patient', 'patients', 'someone', 'anyone',
    'find', 'show', 'list', 'tell', 'give', 'need', 'needs', 'want'
]);

// Punctuation is separator, not identity: "cole's" has to reduce to "cole" so it can be matched
// against a recorded surname.
const tokenise = (value: string): string[] => {
    return value.toLowerCase().replace(/[^a-z0-9]+/gi, ' ').trim().split(' ').filter(Boolean);
};

/**
 * The words in a caseload query that could name a client.
 *
 * A single word is taken as typed, however short, because that is someone filtering the list by name
 * and "co" should still reach Cole. Anything longer is treated as possibly a question, so the filler
 * is stripped: the useful case is a name buried in a sentence ("find margaret cole's DNR"), and
 * matching on the whole string misses it entirely.
 */
export const buildNameQueryTokens = (query: string): string[] => {
    const tokens = tokenise(query);
    if (tokens.length <= 1) {
        return tokens;
    }
    return tokens.filter(token => token.length >= 3 && !QUESTION_WORDS.has(token));
};

/**
 * Whether a client's name is one of the things the query is asking about. Prefix matching runs both
 * ways: "mar" reaches Margaret, and "cole's" reaches Cole once the possessive is stripped.
 */
export const matchesNameQuery = (name: string, queryTokens: string[]): boolean => {
    if (queryTokens.length === 0) {
        return false;
    }

    const nameTokens = tokenise(name);
    return queryTokens.some(queryToken => nameTokens.some(nameToken => {
        return nameToken.startsWith(queryToken) || queryToken.startsWith(nameToken);
    }));
};
